import React, { useEffect } from "react";

import { ArrowLeft, ChevronRight } from "lucide-react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import AccountOverview from "./components/AccountOverview";
import AssessmentsTrendsCard from "./components/AssessmentsTrends";
import BriefingsCard from "./components/Briefings";
import AccountWorkspaceKpis, {
  useAccountHawkeyeMetrics,
} from "./components/Kpis";
import StakeholdersMappingCard from "./components/StakeholdersMapping";
import TeamsCard from "./components/Teams";
import TipsAndRecommendationsCard from "./components/TipsAndRecommendations";
import WorkspacePlaceholderCard from "./components/WorkspacePlaceholderCard";
import Button from "../../Components/Button";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  clearAccountSummary,
  fetchAccountSummary,
} from "../../Store/Reducers/AccountWorkspaceSlice";

/** Fixed height so Account Overview, KPIs, and Teams stay aligned on row 1. */
const TOP_ROW_PANEL_HEIGHT_CLASS = "h-[38vh] lg:h-[42vh]";

const COMING_SOON_SECTIONS = [
  "Goals and Milestones",
  "Playbooks and Game Plans",
] as const;

type WorkspaceLocationState = {
  from?: string;
  fromLabel?: string;
};

const AccountWorkspace: React.FC = () => {
  const { id: accountId, orgSlug } = useParams<{
    id: string;
    orgSlug?: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const locationState = (location.state as WorkspaceLocationState | null) ?? {};
  const { accountSummary, loading, error } = useAppSelector(
    state => state.accountWorkspace
  );

  const withOrg = (path: string) => {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return orgSlug ? `/${orgSlug}${normalizedPath}` : normalizedPath;
  };

  useEffect(() => {
    if (!accountId) return;
    dispatch(fetchAccountSummary(accountId));
    return () => {
      dispatch(clearAccountSummary());
    };
  }, [accountId, dispatch]);

  const pageTitle = accountSummary?.account_name
    ? `${accountSummary.account_name}`
    : "Account Workspace";

  const backPath = locationState.from ?? withOrg("/always-on-channel");
  const backLabel = locationState.fromLabel ?? "Always On Intelligence";

  /** Trail matches entry point: Exec Ops > Accounts, or My Accounts, etc. */
  const fromOpsAccounts = backLabel === "Accounts" || backPath.includes("/ops");
  const breadcrumbTrail: { title: string; to?: string }[] = [
    ...(fromOpsAccounts ? [{ title: "Exec Ops", to: withOrg("/ops") }] : []),
    { title: backLabel, to: backPath },
    { title: "Account Workspace" },
  ];

  const hawkeye = useAccountHawkeyeMetrics({
    accountId,
    accountName: accountSummary?.account_name,
  });

  return (
    <div className="mt-3 min-h-[calc(100vh-4rem)] overflow-hidden bg-white">
      <div className="z-[1] border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2 px-4 py-3">
          <Button
            variant="clear"
            onClick={() => navigate(backPath)}
            className="min-w-0 shrink-0 p-1"
            aria-label={`Back to ${backLabel}`}
          >
            <ArrowLeft size={15} color="#5F6368" />
          </Button>
          <h1 className="m-0 truncate text-xl font-bold leading-tight text-gray-900">
            {pageTitle}
          </h1>
        </div>

        <nav
          aria-label="Breadcrumb"
          className="border-t border-gray-100 bg-slate-50 px-4 py-2.5"
        >
          <ol className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0 text-sm leading-5">
            {breadcrumbTrail.map((crumb, idx) => {
              const isLast = idx === breadcrumbTrail.length - 1;
              return (
                <React.Fragment key={`${crumb.title}-${idx}`}>
                  {idx > 0 ? (
                    <li
                      className="inline-flex items-center text-gray-400"
                      aria-hidden
                    >
                      <ChevronRight className="size-3.5 stroke-[2]" />
                    </li>
                  ) : null}
                  <li
                    className={
                      isLast
                        ? "font-semibold text-gray-900"
                        : "font-normal text-gray-500"
                    }
                    aria-current={isLast ? "page" : undefined}
                  >
                    {crumb.to && !isLast ? (
                      <Link
                        to={crumb.to}
                        className="font-normal text-gray-500 no-underline transition-colors hover:text-[#7C3AED]"
                      >
                        {crumb.title}
                      </Link>
                    ) : (
                      crumb.title
                    )}
                  </li>
                </React.Fragment>
              );
            })}
          </ol>
        </nav>
      </div>

      <div className="relative min-h-[200px]">
        {error && (
          <p
            className="m-0 mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
            role="alert"
          >
            {error}
          </p>
        )}
        {accountId && (
          <div className="w-full bg-white p-3 md:p-4">
            <div className="rounded-md bg-gray-100 p-4 md:p-4">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className={`min-w-0 ${TOP_ROW_PANEL_HEIGHT_CLASS}`}>
                  <AccountOverview
                    account={loading ? null : accountSummary}
                    hawkeye={hawkeye}
                    className="h-full"
                  />
                </div>

                <div className={`min-w-0 ${TOP_ROW_PANEL_HEIGHT_CLASS}`}>
                  <AccountWorkspaceKpis hawkeye={hawkeye} className="h-full" />
                </div>

                <div className={`min-w-0 ${TOP_ROW_PANEL_HEIGHT_CLASS}`}>
                  <TeamsCard
                    accountId={accountId}
                    accountName={accountSummary?.account_name}
                    className="h-full"
                  />
                </div>

                <BriefingsCard className="min-w-0 h-full" />

                <AssessmentsTrendsCard
                  accountId={accountId}
                  className="min-w-0 h-full"
                />

                <StakeholdersMappingCard
                  accountId={accountId}
                  className="min-w-0 h-full"
                />

                <TipsAndRecommendationsCard className="min-w-0 h-full" />

                <WorkspacePlaceholderCard
                  title={COMING_SOON_SECTIONS[0]}
                  className="min-w-0 h-full"
                  minHeight="min-h-[280px]"
                />

                <WorkspacePlaceholderCard
                  title={COMING_SOON_SECTIONS[1]}
                  className="min-w-0 h-full"
                  minHeight="min-h-[280px]"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountWorkspace;
